import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  TopToolbar,
  ExportButton,
  ChipField,
  FunctionField,
  Filter,
  SelectInput,
  SearchInput,
} from 'react-admin';
import ReactMarkdown from 'react-markdown';

const roleChoices = [
  { id: 'user', name: '👤 Пользователь' },
  { id: 'assistant', name: '🤖 Ассистент' },
  { id: 'system', name: '⚙️ Система' },
];

const MessageFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    <SelectInput
      source="role"
      choices={roleChoices}
      label="Роль"
    />
  </Filter>
);

const ListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

const RoleChip = ({ record }: any) => {
  console.log('🔍 [RoleChip] Full Record:', {
    record,
    keys: record ? Object.keys(record) : 'no record',
    role: record?.role,
    content: record?.content,
    createdAt: record?.createdAt
  });
  if (!record) return <span>No record</span>;
  if (!record.role) return <span>role: {JSON.stringify(record.role)}</span>;
  const roleChoice = roleChoices.find(choice => choice.id === record.role);
  return <ChipField record={{ value: roleChoice?.name || record.role }} source="value" />;
};

const ContentPreview = ({ record }: any) => {
  console.log('🔍 [ContentPreview] Record content:', record?.content);
  if (!record) return <span style={{ color: '#999' }}>No record</span>;
  if (!record.content) return <span style={{ color: '#999' }}>Без содержимого</span>;
  
  const preview = record.content.length > 150 
    ? record.content.substring(0, 150) + '...' 
    : record.content;
    
  return (
    <div style={{ 
      maxWidth: '400px',
      fontSize: '0.9em',
      lineHeight: '1.4'
    }}>
      <ReactMarkdown
        components={{
          // Упрощенные стили для превью
          code: ({ node, inline, children, ...props }: any) => {
            if (inline) {
              return (
                <code
                  style={{
                    backgroundColor: '#f1f3f4',
                    padding: '1px 4px',
                    borderRadius: '3px',
                    fontSize: '0.85em',
                    fontFamily: 'monospace'
                  }}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                style={{
                  backgroundColor: '#f8f8f8',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontSize: '0.85em',
                  fontFamily: 'monospace',
                  display: 'inline-block'
                }}
                {...props}
              >
                {children}
              </code>
            );
          },
          // Убираем заголовки для превью
          h1: ({ children }) => <strong>{children}</strong>,
          h2: ({ children }) => <strong>{children}</strong>,
          h3: ({ children }) => <strong>{children}</strong>,
          // Упрощенные списки
          ul: ({ children }) => <span>• {children}</span>,
          ol: ({ children }) => <span>1. {children}</span>,
          li: ({ children }) => <span>{children} </span>,
          // Упрощенные ссылки
          a: ({ children }) => <span style={{ color: '#1a73e8' }}>{children}</span>,
          // Упрощенные цитаты
          blockquote: ({ children }) => <span style={{ fontStyle: 'italic', color: '#666' }}>"{children}"</span>,
          // Убираем переносы строк в превью
          p: ({ children }) => <span>{children} </span>,
        }}
      >
        {preview}
      </ReactMarkdown>
    </div>
  );
};

const DisplayDataPreview = ({ record }: any) => {
  if (!record?.displayData) return <span style={{ color: '#999' }}>-</span>;
  
  try {
    const data = JSON.parse(record.displayData);
    return (
      <span style={{ fontSize: '0.8em', color: '#666' }}>
        📊 {Object.keys(data).length} поля
      </span>
    );
  } catch {
    return <span style={{ color: '#999' }}>Некорректные данные</span>;
  }
};

const ToolCallsPreview = ({ record }: any) => {
  if (!record?.toolCalls) return <span style={{ color: '#999' }}>-</span>;
  
  try {
    const calls = JSON.parse(record.toolCalls);
    if (Array.isArray(calls)) {
      return (
        <span style={{ fontSize: '0.8em', color: '#666' }}>
          🔧 {calls.length} вызовов
        </span>
      );
    }
    return <span style={{ fontSize: '0.8em', color: '#666' }}>🔧 Данные</span>;
  } catch {
    return <span style={{ color: '#999' }}>Некорректные данные</span>;
  }
};

export const ChatMessageList = () => (
  <List
    filters={<MessageFilter />}
    actions={<ListActions />}
    sort={{ field: 'createdAt', order: 'DESC' }}
    perPage={25}
  >
    <Datagrid>
      <TextField source="id" label="ID" />
      
      {/* Роль отправителя */}
      <FunctionField 
        label="Роль" 
        render={(record: any) => {
          console.log('🔍 [RoleField] Record data:', record);
          return <RoleChip record={record} />;
        }}
      />
      
      {/* Пользователь */}
      <FunctionField 
        label="Пользователь" 
        render={(record: any) => record?.user?.name || 'Система'} 
      />
      <FunctionField 
        label="Email" 
        render={(record: any) => record?.user?.email || '-'} 
      />
      
      {/* Содержимое сообщения */}
      <FunctionField 
        label="Содержимое" 
        render={(record: any) => {
          console.log('🔍 [ContentField] Record data:', record);
          return <ContentPreview record={record} />;
        }}
      />
      
      {/* Дополнительные данные */}
      <FunctionField 
        label="Display Data" 
        render={(record: any) => <DisplayDataPreview record={record} />}
      />
      <FunctionField 
        label="Tool Calls" 
        render={(record: any) => <ToolCallsPreview record={record} />}
      />
      
      {/* Даты */}
      <DateField source="createdAt" label="Создано" showTime />
      <DateField source="updatedAt" label="Обновлено" showTime />
    </Datagrid>
  </List>
);
